
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  supabase,
  uploadImage,
  deleteImage,
  adminOperation,
} from '../../utils/supabase';
import type {
  ShelterType as Shelter,
  UserType as User,
  ProductType as Product,
} from '../../utils/supabase';

interface MisionHuellitas {
  id: string;
  title: string;
  description: string;
  city?: string;
  image_url?: string;
  image_url_2?: string;
  image_url_3?: string;
  image_url_4?: string;
  goal: number;
  raised: number;
  urgent: boolean;
  active: boolean;
  status: 'active' | 'upcoming' | 'completed';
  start_date: string;
  end_date?: string;
  beneficiaries_count: number;
  participation_link?: string;
  created_at: string;
  updated_at: string;
}

// New interface for lost pet reports
interface LostPetReport {
  id: string;
  reporter_name?: string;
  reporter_phone?: string;
  pet_description: string;
  location_description: string;
  image_url?: string;
  status: 'pending' | 'approved' | 'rejected';
  category: 'perdido' | 'abandonado' | 'herido';
  admin_notes?: string;
  resolved: boolean;
  created_at: string;
  updated_at: string;
}

export default function MasterAdmin() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('refugios');
  const [shelters, setShelters] = useState<Shelter[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [misionHuellitas, setMisionHuellitas] = useState<MisionHuellitas[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [lostPetReports, setLostPetReports] = useState<LostPetReport[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [modalType, setModalType] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Nuevo estado para información de contacto
  const [contactInfo, setContactInfo] = useState({
    contact_address: '',
    contact_email: '',
    contact_whatsapp: '',
    contact_facebook: '',
  });
  const [showContactModal, setShowContactModal] = useState(false);
  const [savingContact, setSavingContact] = useState(false);

  /* ------------------------------------------------------------------------
   * Session handling
   * --------------------------------------------------------------------- */
  const checkSessionValidity = () => {
    const sessionData = localStorage.getItem('user_session');
    if (!sessionData) return null;

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();

      // 3‑hour expiry
      if (now > session.expiresAt) {
        localStorage.removeItem('user_session');
        return null;
      }
      return session;
    } catch {
      localStorage.removeItem('user_session');
      return null;
    }
  };

  // Load user session
  useEffect(() => {
    const validSession = checkSessionValidity();
    if (validSession && validSession.plan === 'master') {
      setCurrentUser(validSession);
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // Load all data when a user is present
  useEffect(() => {
    if (currentUser) {
      loadAllData();
      loadContactInfo();
    }
  }, [currentUser]);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadShelters(),
      loadUsers(),
      loadMisionHuellitas(),
      loadProducts(),
      loadLostPetReports(),
    ]);
    setLoading(false);
  };

  // Data loaders
  const loadShelters = async () => {
    try {
      const { data, error } = await supabase
        .from('shelters')
        .select(`
          *,
          users!inner(shelter_name, city, state, phone, created_at)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setShelters(data || []);
    } catch (err) {
      console.error('Error loading shelters', err);
    }
  };

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .neq('plan', 'master')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Error loading users', err);
    }
  };

  const loadMisionHuellitas = async () => {
    try {
      const { data, error } = await supabase
        .from('mision_huellitas')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMisionHuellitas(data || []);
    } catch (err) {
      console.error('Error loading mision huellitas', err);
    }
  };

  const loadProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (err) {
      console.error('Error loading products', err);
    }
  };

  const loadLostPetReports = async () => {
    try {
      const { data, error } = await supabase
        .from('lost_pets_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLostPetReports(data || []);
    } catch (err) {
      console.error('Error loading lost pet reports', err);
    }
  };

  // Nueva función para cargar información de contacto
  const loadContactInfo = async () => {
    try {
      const { data, error } = await supabase
        .from('master_admins')
        .select('contact_address, contact_email, contact_whatsapp, contact_facebook')
        .limit(1)
        .single();

      if (error) {
        console.error('Error cargando información de contacto:', error);
      } else if (data) {
        setContactInfo({
          contact_address: data.contact_address || '',
          contact_email: data.contact_email || '',
          contact_whatsapp: data.contact_whatsapp || '',
          contact_facebook: data.contact_facebook || '',
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Nueva función para guardar información de contacto
  const saveContactInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingContact(true);

    try {
      const { error } = await supabase
        .from('master_admins')
        .update({
          contact_address: contactInfo.contact_address || null,
          contact_email: contactInfo.contact_email || null,
          contact_whatsapp: contactInfo.contact_whatsapp || null,
          contact_facebook: contactInfo.contact_facebook || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', currentUser?.id);

      if (error) {
        throw error;
      }

      alert('Información de contacto guardada exitosamente');
      setShowContactModal(false);
    } catch (error) {
      console.error('Error guardando información de contacto:', error);
      alert('Error al guardar la información de contacto');
    } finally {
      setSavingContact(false);
    }
  };

  /* ------------------------------------------------------------------------
   * Form submit
   * --------------------------------------------------------------------- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    // Prevenir envío si se están subiendo imágenes
    if (uploadingImage) {
      alert('Por favor espera a que terminen de subirse las imágenes');
      return;
    }

    const formData = new FormData(e.target as HTMLFormElement);
    const data = Object.fromEntries(formData.entries());

    try {
      // Usar las imágenes de imagePreview directamente
      const images = [...imagePreview];

      let operationData: any;
      let tableName: string = '';

      if (modalType === 'mision') {
        tableName = 'mision_huellitas';
        operationData = {
          title: data.title as string,
          description: data.description as string,
          city: data.city as string,
          image_url: images[0] || null,
          image_url_2: images[1] || null,
          image_url_3: images[2] || null,
          image_url_4: images[3] || null,
          goal: parseFloat(data.goal as string),
          raised: parseFloat(data.raised as string) || 0,
          urgent: data.urgent === 'on',
          active: data.active === 'on',
          start_date: data.start_date as string,
          participation_link: data.participation_link as string,
        };
      } else if (modalType === 'producto') {
        tableName = 'products';
        operationData = {
          name: data.name as string,
          description: data.description as string,
          price: parseFloat(data.price as string),
          image_url: images[0] || null,
          image_url_2: images[1] || null,
          image_url_3: images[2] || null,
          image_url_4: images[3] || null,
          active: data.active === 'on',
        };
      }

      console.log('Datos a enviar:', operationData);

      let result;
      if (editingItem?.id) {
        console.log('Actualizando elemento con ID:', editingItem.id);
        result = await adminOperation('update', tableName, operationData, editingItem.id);
      } else {
        console.log('Creando nuevo elemento');
        result = await adminOperation('insert', tableName, operationData);
      }

      console.log('Resultado de la operación:', result);

      if (result?.error) {
        console.error('Error en operación:', result.error);
        alert(`Error específico: ${JSON.stringify(result.error)}`);
        return;
      }

      if (!result?.data) {
        console.error('No se recibieron datos en la respuesta');
        alert('Error: No se recibieron datos en la respuesta del servidor');
        return;
      }

      if (modalType === 'mision') {
        await loadMisionHuellitas();
      } else if (modalType === 'producto') {
        await loadProducts();
      }

      closeModal();
      alert(`${modalType === 'mision' ? 'Misión' : 'Producto'} guardado exitosamente`);
    } catch (err) {
      console.error('Error completo:', err);
      alert(`Error al guardar: ${(err as Error)?.message || 'Error desconocido'}`);
    }
  };

  /* ------------------------------------------------------------------------
   * Delete handling
   * --------------------------------------------------------------------- */
  const deleteItem = async (type: string, id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este elemento?')) return;

    try {
      let itemToDelete: any = null;
      let tableName: string = '';

      if (type === 'mision') {
        itemToDelete = misionHuellitas.find((i) => i.id === id);
        tableName = 'mision_huellitas';
      } else if (type === 'producto') {
        itemToDelete = products.find((i) => i.id === id);
        tableName = 'products';
      } else if (type === 'huellita-perdida') {
        itemToDelete = lostPetReports.find((i) => i.id === id);
        tableName = 'lost_pets_reports';
      } else {
        throw new Error('Tipo de elemento no válido');
      }

      // Delete image first (if stored in Supabase)
      if (itemToDelete?.image_url?.includes('supabase')) {
        await deleteImage(itemToDelete.image_url);
      }

      const result = await adminOperation('delete', tableName, null, id);
      if (result?.error) throw result.error;

      if (type === 'mision') {
        await loadMisionHuellitas();
      } else if (type === 'producto') {
        await loadProducts();
      } else if (type === 'huellita-perdida') {
        await loadLostPetReports();
      }

      alert('Eliminado exitosamente');
    } catch (err) {
      console.error('Error deleting item', err);
      alert('Error al eliminar');
    }
  };

  /* ------------------------------------------------------------------------
   * UI helpers
   * --------------------------------------------------------------------- */
  const openModal = (type: string, item?: any) => {
    setModalType(type);
    setEditingItem(item || null);

    // Configurar imágenes existentes
    if (item) {
      const existingImages = [
        item.image_url,
        item.image_url_2,
        item.image_url_3,
        item.image_url_4,
      ].filter(Boolean);

      setImagePreview(existingImages);
    } else {
      setImagePreview([]);
    }

    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setModalType('');
    setImagePreview([]);
  };

  const formatNumber = (num: number) => new Intl.NumberFormat('es-MX').format(num);

  const handleLogout = () => {
    localStorage.removeItem('user_session');
    navigate('/login');
  };

  /* ------------------------------------------------------------------------
   * Image upload
   * --------------------------------------------------------------------- */
  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Obtener imágenes actuales
    const currentImages = imagePreview.length;
    const maxImages = 4;
    const totalImages = currentImages + files.length;

    if (totalImages > maxImages) {
      alert(`Solo puedes subir máximo ${maxImages} fotos. Actualmente tienes ${currentImages} foto(s).`);
      return;
    }

    // Validar cada archivo
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        alert('Por favor selecciona solo archivos de imagen válidos');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Cada imagen debe ser menor a 5MB');
        return;
      }
    }

    setUploadingImage(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const folderName = modalType === 'producto' ? 'productos' : 'mision-huellitas';
        const uploadedUrl = await uploadImage(file, folderName);
        return uploadedUrl;
      });

      const uploadedUrls = await Promise.all(uploadPromises);
      const validUrls = uploadedUrls.filter((url) => url !== null);

      if (validUrls.length > 0) {
        // Agregar las nuevas URLs a la vista previa
        setImagePreview((prev) => [...prev, ...validUrls]);
      } else {
        alert('Error al subir las imágenes');
      }
    } catch (err) {
      console.error('Error uploading images', err);
      alert('Error al subir las imágenes');
    } finally {
      setUploadingImage(false);
    }
  };

  // Función para remover imagen de la vista previa
  const removeImage = (indexToRemove: number) => {
    setImagePreview((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // Functions for lost pet reports
  const updateReportStatus = async (
    reportId: string,
    newStatus: 'approved' | 'rejected',
    adminNotes?: string,
  ) => {
    try {
      const { error } = await supabase
        .from('lost_pets_reports')
        .update({
          status: newStatus,
          admin_notes: adminNotes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', reportId);

      if (error) throw error;

      await loadLostPetReports();
      alert(`Reporte ${newStatus === 'approved' ? 'aprobado' : 'rechazado'} exitosamente`);
    } catch (error) {
      console.error('Error updating report status:', error);
      alert('Error al actualizar el estado del reporte');
    }
  };

  const markReportAsResolved = async (reportId: string) => {
    try {
      const { error } = await supabase
        .from('lost_pets_reports')
        .update({
          resolved: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', reportId);

      if (error) throw error;

      await loadLostPetReports();
      alert('Caso marcado como resuelto exitosamente');
    } catch (error) {
      console.error('Error marking report as resolved:', error);
      alert('Error al marcar el caso como resuelto');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'perdido':
        return 'ri-search-line';
      case 'abandonado':
        return 'ri-alarm-warning-line';
      case 'herido':
        return 'ri-heart-pulse-line';
      default:
        return 'ri-heart-line';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'perdido':
        return 'bg-blue-100 text-blue-800';
      case 'abandonado':
        return 'bg-red-100 text-red-800';
      case 'herido':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'perdido':
        return 'Perdido';
      case 'abandonado':
        return 'Abandonado';
      case 'herido':
        return 'Herido';
      default:
        return 'Desconocido';
    }
  };

  /* ------------------------------------------------------------------------
   * Render
   * --------------------------------------------------------------------- */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-orange-500 animate-spin mb-4"></i>
          <p className="text-gray-600">Cargando panel de administración maestro...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <i className="ri-shield-star-fill text-white text-lg"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Panel Administrador Maestro</h1>
                <p className="text-sm text-gray-500">Huellitas Unidas - Control Total</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
              >
                <i className="ri-home-line"></i>
                <span>Volver al Sitio</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:text-red-800 transition-colors cursor-pointer"
              >
                <i className="ri-logout-box-line"></i>
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('refugios')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'refugios'
                  ? 'border-orange-500 text-orange-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-building-line mr-2"></i>
              Registro de Refugios ({shelters.length})
            </button>

            <button
              onClick={() => setActiveTab('mision-huellitas')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'mision-huellitas'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-heart-3-line mr-2"></i>
              Misión Huellitas ({misionHuellitas.length})
            </button>

            <button
              onClick={() => setActiveTab('tienda')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'tienda'
                  ? 'border-green-500 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-store-line mr-2"></i>
              Tienda ({products.length})
            </button>

            <button
              onClick={() => setActiveTab('huellitas-perdidas')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'huellitas-perdidas'
                  ? 'border-red-500 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-search-eye-line mr-2"></i>
              Huellitas Perdidas ({lostPetReports.filter((r) => r.status === 'pending').length})
            </button>

            <button
              onClick={() => setActiveTab('contacto')}
              className={`py-4 px-2 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === 'contacto'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <i className="ri-phone-line mr-2"></i>
              Información de Contacto
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ----------------------  REFUGIOS TAB  ---------------------- */}
        {activeTab === 'refugios' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Refugios Registrados</h2>
              <div className="text-sm text-gray-500">Total de usuarios registrados: {users.length}</div>
            </div>

            {shelters.length > 0 ? (
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Refugio
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Usuario
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contacto
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ubicación
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Capacidad
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fecha Registro
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Estado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {shelters.map((s) => (
                        <tr key={s.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-12 w-12">
                                <img
                                  className="h-12 w-12 rounded-lg object-cover"
                                  src={
                                    s.image_url ||
                                    'https://readdy.ai/api/search-image?query=Animal%20shelter%20building%20with%20dogs%20and%20volunteers%2C%20warm%20community%20atmosphere%20with%20rescue%20animals&width=100&height=100&seq=shelter-default&orientation=squarish'
                                  }
                                  alt={s.name}
                                />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{s.name}</div>
                                <div className="text-sm text-gray-500 line-clamp-1">{s.description}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">N/A</div>
                            <div className="text-sm text-gray-500">N/A</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{s.contact}</div>
                            <div className="text-sm text-gray-500">{s.contact_phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{s.location}</div>
                            <div className="text-sm text-gray-500">
                              N/A, N/A
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {s.current_dogs}/{s.capacity}
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                              <div
                                className="bg-green-500 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(100, (s.current_dogs / s.capacity) * 100)}%`,
                                }}
                              ></div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(s.created_at).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                s.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {s.active ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <i className="ri-building-line text-4xl text-orange-300 mb-4"></i>
                <p className="text-gray-500">No hay refugios registrados</p>
              </div>
            )}
          </section>
        )}

        {/* ----------------------  MISION HUELLITAS TAB  ---------------------- */}
        {activeTab === 'mision-huellitas' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Misión Huellitas</h2>
              <button
                onClick={() => openModal('mision')}
                className="bg-purple-500 text-white px-4 py-2 rounded-lg hover:bg-purple-600 transition-colors"
              >
                <i className="ri-add-line mr-2"></i> Nueva Misión
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {misionHuellitas.map((item) => {
                const images = [item.image_url, item.image_url_2, item.image_url_3, item.image_url_4].filter(Boolean);
                return (
                  <div key={item.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="relative">
                      <img
                        src={
                          images[0] ||
                          'https://readdy.ai/api/search-image?query=Heartwarming%20animal%20rescue%20mission%20with%20volunteers%20helping%20dogs%2C%20community%20support%20for%20animal%20welfare%2C%20hope%20and%20compassion&width=400&height=240&seq=mision-default&orientation=landscape'
                        }
                        alt={item.title}
                        className="w-full h-48 object-cover"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                          <i className="ri-image-line mr-1"></i>
                          {images.length}
                        </div>
                      )}
                      <div
                        className={`absolute top-2 left-2 px-3 py-1 rounded text-xs font-semibold ${
                          item.active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                        }`}
                      >
                        {item.active ? 'Activa' : 'Desactivada'}
                      </div>
                      {item.urgent && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">
                          URGENTE
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">{item.description}</p>

                      <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                        <span>Meta: ${formatNumber(item.goal)}</span>
                        <span>{item.beneficiaries_count} beneficiarios</span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                        <div
                          className="bg-purple-500 h-2 rounded-full"
                          style={{ width: `${Math.min(100, (item.raised / item.goal) * 100)}%` }}
                        ></div>
                      </div>

                      <div className="flex justify-between items-center text-sm mb-4">
                        <span className="text-purple-600 font-medium">
                          Recaudado: ${formatNumber(item.raised)}
                        </span>
                        <span className="text-gray-500">
                          {Math.round((item.raised / item.goal) * 100)}%
                        </span>
                      </div>

                      <div className="text-xs text-gray-500 mb-4">
                        <div>Inicio: {new Date(item.start_date).toLocaleDateString('es-ES')}</div>
                        {item.end_date && <div>Fin: {new Date(item.end_date).toLocaleDateString('es-ES')}</div>}
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={() => openModal('mision', item)}
                          className="text-purple-600 hover:text-purple-800"
                        >
                          <i className="ri-edit-line mr-1"></i>Editar
                        </button>
                        <button
                          onClick={() => deleteItem('mision', item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {misionHuellitas.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <i className="ri-heart-3-line text-4xl text-purple-300 mb-4"></i>
                  <p className="text-gray-500">No hay misiones creadas</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ----------------------  TIENDA TAB  ---------------------- */}
        {activeTab === 'tienda' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Productos de la Tienda</h2>
              <button
                onClick={() => openModal('producto')}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
              >
                <i className="ri-add-line mr-2"></i> Nuevo Producto
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product) => {
                const images = [
                  product.image_url,
                  product.image_url_2,
                  product.image_url_3,
                  product.image_url_4,
                ].filter(Boolean);
                return (
                  <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden">
                    <div className="relative">
                      <img
                        src={
                          images[0] ||
                          'https://readdy.ai/api/search-image?query=Pet%20product%20merchandise%20for%20animal%20shelter%20fundraising%2C%20cute%20dog%20accessories%20and%20toys%2C%20heartwarming%20pet%20store%20items&width=400&height=240&seq=product-default&orientation=landscape'
                        }
                        alt={product.name}
                        className="w-full h-48 object-cover"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs">
                          <i className="ri-image-line mr-1"></i>
                          {images.length}
                        </div>
                      )}
                      <div
                        className={`absolute top-2 left-2 px-3 py-1 rounded text-xs font-semibold ${
                          product.active ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'
                        }`}
                      >
                        {product.active ? 'Disponible' : 'No Disponible'}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">{product.name}</h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-3">{product.description}</p>

                      <div className="flex justify-between items-center mb-4">
                        <span className="text-2xl font-bold text-green-600">
                          ${formatNumber(product.price)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(product.created_at).toLocaleDateString('es-ES')}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <button
                          onClick={() => openModal('producto', product)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <i className="ri-edit-line mr-1"></i>Editar
                        </button>
                        <button
                          onClick={() => deleteItem('producto', product.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {products.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <i className="ri-store-line text-4xl text-green-300 mb-4"></i>
                  <p className="text-gray-500">No hay productos creados</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* ----------------------  HUELLITAS PERDIDAS TAB  ---------------------- */}
        {activeTab === 'huellitas-perdidas' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Reportes de Huellitas Perdidas</h2>
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-500">
                  Pendientes: {lostPetReports.filter((r) => r.status === 'pending').length} | Aprobados:{' '}
                  {lostPetReports.filter((r) => r.status === 'approved').length} | Resueltos:{' '}
                  {lostPetReports.filter((r) => r.resolved).length}
                </div>
                <button
                  onClick={() => navigate('/huellitas-perdidas')}
                  className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors cursor-pointer"
                >
                  <i className="ri-external-link-line mr-2"></i>Ver Página Pública
                </button>
              </div>
            </div>

            {lostPetReports.length > 0 ? (
              <div className="space-y-6">
                {/* Reportes Pendientes */}
                {lostPetReports.filter((r) => r.status === 'pending').length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-time-line mr-2 text-yellow-500"></i>
                      Reportes Pendientes de Revisión ({lostPetReports.filter((r) => r.status === 'pending').length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {lostPetReports
                        .filter((r) => r.status === 'pending')
                        .map((report) => (
                          <div key={report.id} className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-yellow-500">
                            <div className="relative">
                              <img
                                src={
                                  report.image_url ||
                                  'https://readdy.ai/api/search-image?query=Lost%20or%20abandoned%20dog%20needing%20help%2C%20sad%20rescue%20dog%20looking%20for%20home%2C%20heartwarming%20animal%20rescue%20scene&width=400&height=240&seq=lost-pet-admin&orientation=landscape'
                                }
                                alt="Reporte"
                                className="w-full h-48 object-cover"
                              />
                              <div className={`absolute top-2 left-2 px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(report.category)}`}>
                                <i className={`${getCategoryIcon(report.category)} mr-1`}></i>
                                {getCategoryLabel(report.category)}
                              </div>
                              <div className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                                PENDIENTE
                              </div>
                            </div>
                            <div className="p-4">
                              <div className="flex items-center text-gray-600 mb-2">
                                <i className="ri-map-pin-fill mr-2 text-red-500"></i>
                                <span className="font-medium text-sm">{report.location_description}</span>
                              </div>
                              <p className="text-gray-700 text-sm mb-3 line-clamp-3">{report.pet_description}</p>

                              {report.reporter_name && (
                                <div className="text-xs text-gray-500 mb-2">
                                  <i className="ri-user-line mr-1"></i>
                                  Reportado por: {report.reporter_name}
                                </div>
                              )}

                              {report.reporter_phone && (
                                <div className="text-xs text-gray-500 mb-3">
                                  <i className="ri-phone-line mr-1"></i>
                                  {report.reporter_phone}
                                </div>
                              )}

                              <div className="text-xs text-gray-400 mb-4">
                                {new Date(report.created_at).toLocaleDateString('es-ES', {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </div>

                              <div className="flex space-x-2">
                                <button
                                  onClick={() => updateReportStatus(report.id, 'approved')}
                                  className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer"
                                >
                                  <i className="ri-check-line mr-1"></i>
                                  Aprobar
                                </button>

                                <button
                                  onClick={() => {
                                    const reason = prompt('¿Por qué rechazas este reporte? (opcional)');
                                    updateReportStatus(report.id, 'rejected', reason || undefined);
                                  }}
                                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer"
                                >
                                  <i className="ri-close-line mr-1"></i>
                                  Rechazar
                                </button>

                                <button
                                  onClick={() => deleteItem('huellita-perdida', report.id)}
                                  className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Reportes Aprobados */}
                {lostPetReports.filter((r) => r.status === 'approved' && !r.resolved).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-check-line mr-2 text-green-500"></i>
                      Casos Activos Publicados ({lostPetReports.filter((r) => r.status === 'approved' && !r.resolved).length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {lostPetReports
                        .filter((r) => r.status === 'approved' && !r.resolved)
                        .map((report) => (
                          <div key={report.id} className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-green-500">
                            <div className="relative">
                              <img
                                src={
                                  report.image_url ||
                                  'https://readdy.ai/api/search-image?query=Lost%20or%20abandoned%20dog%20needing%20help%2C%20sad%20rescue%20dog%20looking%20for%20home%2C%20heartwarming%20animal%20rescue%20scene&width=400&height=240&seq=lost-pet-admin&orientation=landscape'
                                }
                                alt="Reporte"
                                className="w-full h-48 object-cover"
                              />
                              <div className={`absolute top-2 left-2 px-3 py-1 rounded-full text-xs font-semibold ${getCategoryColor(report.category)}`}>
                                <i className={`${getCategoryIcon(report.category)} mr-1`}></i>
                                {getCategoryLabel(report.category)}
                              </div>
                              <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                                PUBLICADO
                              </div>
                            </div>
                            <div className="p-4">
                              <div className="flex items-center text-gray-600 mb-2">
                                <i className="ri-map-pin-fill mr-2 text-red-500"></i>
                                <span className="font-medium text-sm">{report.location_description}</span>
                              </div>
                              <p className="text-gray-700 text-sm mb-3 line-clamp-3">{report.pet_description}</p>

                              <div className="text-xs text-gray-400 mb-4">
                                Publicado:{' '}
                                {new Date(report.updated_at || report.created_at).toLocaleDateString('es-ES')}
                              </div>

                              <div className="flex space-x-2">
                                <button
                                  onClick={() => {
                                    if (window.confirm('¿Estás seguro de que este caso ha sido resuelto?')) {
                                      markReportAsResolved(report.id);
                                    }
                                  }}
                                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer"
                                >
                                  <i className="ri-check-double-line mr-1"></i>
                                  Marcar como Resuelto
                                </button>

                                <button
                                  onClick={() => deleteItem('huellita-perdida', report.id)}
                                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line mr-1"></i>
                                  Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Casos Resueltos */}
                {lostPetReports.filter((r) => r.resolved).length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-heart-fill mr-2 text-purple-500"></i>
                      Casos Resueltos ({lostPetReports.filter((r) => r.resolved).length})
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {lostPetReports
                        .filter((r) => r.resolved)
                        .slice(0, 6)
                        .map((report) => (
                          <div key={report.id} className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-purple-500 opacity-75">
                            <div className="relative">
                              <img
                                src={
                                  report.image_url ||
                                  'https://readdy.ai/api/search-image?query=Happy%20rescued%20dog%20with%20new%20family%2C%20successful%20animal%20rescue%20story%2C%20heartwarming%20pet%20adoption&width=400&height=240&seq=resolved-pet&orientation=landscape'
                                }
                                alt="Caso resuelto"
                                className="w-full h-48 object-cover"
                              />
                              <div className="absolute top-2 right-2 bg-purple-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                                ✅ RESUELTO
                              </div>
                            </div>
                            <div className="p-4">
                              <div className="flex items-center text-gray-600 mb-2">
                                <i className="ri-map-pin-fill mr-2 text-red-500"></i>
                                <span className="font-medium text-sm">{report.location_description}</span>
                              </div>
                              <p className="text-gray-700 text-sm mb-3 line-clamp-2">{report.pet_description}</p>
                              <div className="text-xs text-gray-400 mb-4">
                                Resuelto: {new Date(report.updated_at).toLocaleDateString('es-ES')}
                              </div>

                              {/* Agregar botón de eliminar para casos resueltos */}
                              <div className="flex justify-center">
                                <button
                                  onClick={() => deleteItem('huellita-perdida', report.id)}
                                  className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-semibold transition-all duration-300 cursor-pointer flex items-center"
                                >
                                  <i className="ri-delete-bin-line mr-2"></i>
                                  Eliminar
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                    {lostPetReports.filter((r) => r.resolved).length > 6 && (
                      <div className="text-center mt-4">
                        <p className="text-gray-500 text-sm">
                          Y {lostPetReports.filter((r) => r.resolved).length - 6} casos
                          resueltos más...
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Reportes Rechazados */}
                {lostPetReports.filter((r) => r.status === 'rejected').length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <i className="ri-close-line mr-2 text-red-500"></i>
                      Reportes Rechazados ({lostPetReports.filter((r) => r.status === 'rejected').length})
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3">
                        {lostPetReports
                          .filter((r) => r.status === 'rejected')
                          .slice(0, 3)
                          .map((report) => (
                            <div key={report.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <img
                                  src={
                                    report.image_url ||
                                    'https://readdy.ai/api/search-image?query=Lost%20or%20abandoned%20dog%20needing%20help%2C%20sad%20rescue%20dog%20looking%20for%20home%2C%20heartwarming%20animal%20rescue%20scene&width=60&height=60&seq=rejected-pet&orientation=squarish'
                                  }
                                  alt="Reporte rechazado"
                                  className="w-12 h-12 object-cover rounded-lg"
                                />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">{report.location_description}</p>
                                  <p className="text-xs text-gray-500 line-clamp-1">{report.pet_description}</p>
                                  {report.admin_notes && (
                                    <p className="text-xs text-red-600 mt-1">Motivo: {report.admin_notes}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center space-x-3">
                                <div className="text-xs text-gray-400">
                                  {new Date(report.updated_at || report.created_at).toLocaleDateString('es-ES')}
                                </div>
                                {/* Agregar botón de eliminar para reportes rechazados */}
                                <button
                                  onClick={() => deleteItem('huellita-perdida', report.id)}
                                  className="bg-red-500 hover:bg-red-600 text-white py-1 px-2 rounded text-xs font-semibold transition-all duration-300 cursor-pointer"
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                      {lostPetReports.filter((r) => r.status === 'rejected').length > 3 && (
                        <div className="text-center mt-3">
                          <p className="text-gray-500 text-sm">
                            Y {lostPetReports.filter((r) => r.status === 'rejected').length - 3} reportes
                            rechazados más...
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <i className="ri-search-eye-line text-4xl text-red-300 mb-4"></i>
                <p className="text-gray-500">No hay reportes de huellitas perdidas</p>
              </div>
            )}
          </section>
        )}

        {/* ----------------------  CONTACTO TAB  ---------------------- */}
        {activeTab === 'contacto' && (
          <section>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Información de Contacto</h2>
                <p className="text-gray-600 text-sm mt-1">
                  Esta información aparecerá en el pie de página de todas las páginas del sitio
                </p>
              </div>
              <button
                onClick={() => setShowContactModal(true)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors cursor-pointer"
              >
                <i className="ri-edit-line mr-2"></i>
                Editar Contacto
              </button>
            </div>

            {/* Vista previa de la información de contacto */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vista Previa del Footer</h3>
              
              <div className="bg-gray-800 text-white p-6 rounded-lg">
                <div className="text-center mb-6">
                  <h4 className="text-lg font-semibold mb-2">Contacto</h4>
                  <p className="text-gray-400 text-sm">¿Tienes preguntas? ¡Estamos aquí para ayudarte!</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Dirección */}
                  {contactInfo.contact_address && (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-map-pin-fill text-xl text-white"></i>
                      </div>
                      <h5 className="font-medium mb-2">Dirección</h5>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {contactInfo.contact_address}
                      </p>
                    </div>
                  )}

                  {/* Email */}
                  {contactInfo.contact_email && (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-mail-fill text-xl text-white"></i>
                      </div>
                      <h5 className="font-medium mb-2">Correo</h5>
                      <p className="text-gray-400 text-sm break-all">
                        {contactInfo.contact_email}
                      </p>
                    </div>
                  )}

                  {/* WhatsApp */}
                  {contactInfo.contact_whatsapp && (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-whatsapp-fill text-xl text-white"></i>
                      </div>
                      <h5 className="font-medium mb-2">WhatsApp</h5>
                      <div className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium mx-auto inline-flex items-center">
                        <i className="ri-whatsapp-fill mr-2"></i>
                        Escribir
                      </div>
                    </div>
                  )}

                  {/* Facebook */}
                  {contactInfo.contact_facebook && (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="ri-facebook-fill text-xl text-white"></i>
                      </div>
                      <h5 className="font-medium mb-2">Facebook</h5>
                      <div className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium mx-auto inline-flex items-center">
                        <i className="ri-facebook-fill mr-2"></i>
                        Seguir
                      </div>
                    </div>
                  )}
                </div>

                {(!contactInfo.contact_address && !contactInfo.contact_email && !contactInfo.contact_whatsapp && !contactInfo.contact_facebook) && (
                  <div className="text-center py-8">
                    <i className="ri-information-line text-4xl text-gray-500 mb-4"></i>
                    <p className="text-gray-400">No hay información de contacto configurada</p>
                    <p className="text-gray-500 text-sm mt-2">Haz clic en "Editar Contacto" para agregar información</p>
                  </div>
                )}
              </div>
            </div>

            {/* Información actual */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Información Actual</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dirección</label>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-900">{contactInfo.contact_address || 'No configurada'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Correo Electrónico</label>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-900">{contactInfo.contact_email || 'No configurado'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp</label>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-900">{contactInfo.contact_whatsapp || 'No configurado'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Facebook</label>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-gray-900">{contactInfo.contact_facebook || 'No configurado'}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* ----------------------  MODAL DE CONTACTO  ---------------------- */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Editar Información de Contacto</h3>
                <button 
                  onClick={() => setShowContactModal(false)} 
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <form onSubmit={saveContactInfo} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dirección
                  </label>
                  <textarea
                    value={contactInfo.contact_address}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, contact_address: e.target.value }))}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: Calle 5 de Mayo #123, Colonia Centro, Ciudad de México, CDMX, CP 06000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Dirección completa que aparecerá en el footer
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Correo Electrónico
                  </label>
                  <input
                    type="email"
                    value={contactInfo.contact_email}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, contact_email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="contacto@huellitasunidas.org"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Email principal de contacto
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp
                  </label>
                  <input
                    type="text"
                    value={contactInfo.contact_whatsapp}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, contact_whatsapp: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="5215512345678 o https://wa.me/5215512345678"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Número de WhatsApp (con código de país) o enlace completo
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Facebook
                  </label>
                  <input
                    type="text"
                    value={contactInfo.contact_facebook}
                    onChange={(e) => setContactInfo(prev => ({ ...prev, contact_facebook: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="https://facebook.com/huellitasunidas o facebook.com/huellitasunidas"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL de la página de Facebook
                  </p>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowContactModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors cursor-pointer"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={savingContact}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-500 rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    {savingContact ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Guardando...
                      </>
                    ) : (
                      'Guardar Información'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ----------------------  MODAL  ---------------------- */}
      {showModal && (modalType === 'mision' || modalType === 'producto') && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  {editingItem ? 'Editar' : 'Crear'}{' '}
                  {modalType === 'mision' ? 'Misión Huellitas' : 'Producto'}
                </h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit}>
                {modalType === 'mision' ? (
                  <>
                    {/* Title */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Título de la Misión *</label>
                      <input
                        name="title"
                        defaultValue={editingItem?.title || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Título de la misión"
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
                      <textarea
                        name="description"
                        defaultValue={editingItem?.description || ''}
                        required
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Descripción detallada de la misión"
                      ></textarea>
                    </div>

                    {/* City */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ciudad *</label>
                      <input
                        name="city"
                        defaultValue={editingItem?.city || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Ciudad donde se desarrolla la misión"
                      />
                    </div>

                    {/* Goal */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meta (MXN) *</label>
                      <input
                        name="goal"
                        type="number"
                        min="1"
                        step="0.01"
                        defaultValue={editingItem?.goal || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Cantidad objetivo"
                      />
                    </div>

                    {/* Raised */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cantidad Recaudada (MXN) *
                      </label>
                      <input
                        name="raised"
                        type="number"
                        min="0"
                        step="0.01"
                        defaultValue={editingItem?.raised || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    {/* Start Date */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de Inicio *</label>
                      <input
                        name="start_date"
                        type="date"
                        defaultValue={
                          editingItem?.start_date
                            ? new Date(editingItem.start_date).toISOString().split('T')[0]
                            : ''
                        }
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    {/* Participation Link */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Enlace de Participación *
                      </label>
                      <input
                        name="participation_link"
                        type="url"
                        defaultValue={editingItem?.participation_link || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="https://ejemplo.com/donar o enlace de pago"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Este enlace se usará en el botón "Participar en la Misión". Puede ser un formulario de
                        donación, página de pago, WhatsApp, etc.
                      </p>
                    </div>

                    {/* Urgent */}
                    <div className="mb-4">
                      <label className="flex items-center">
                        <input
                          name="urgent"
                          type="checkbox"
                          defaultChecked={editingItem?.urgent}
                          className="rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Misión Urgente</span>
                      </label>
                    </div>

                    {/* Active */}
                    <div className="mb-6">
                      <label className="flex items-center">
                        <input
                          name="active"
                          type="checkbox"
                          defaultChecked={editingItem?.active !== false}
                          className="rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {editingItem ? 'Misión Activa (desmarcar para desactivar)' : 'Publicar Misión (se activará automáticamente)'}
                        </span>
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Product Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Producto *</label>
                      <input
                        name="name"
                        defaultValue={editingItem?.name || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Nombre del producto"
                      />
                    </div>

                    {/* Product Description */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
                      <textarea
                        name="description"
                        defaultValue={editingItem?.description || ''}
                        required
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Descripción detallada del producto"
                      ></textarea>
                    </div>

                    {/* Product Price */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Precio (MXN) *</label>
                      <input
                        name="price"
                        type="number"
                        min="0.01"
                        step="0.01"
                        defaultValue={editingItem?.price || ''}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                        placeholder="Precio del producto"
                      />
                    </div>

                    {/* Active */}
                    <div className="mb-6">
                      <label className="flex items-center">
                        <input
                          name="active"
                          type="checkbox"
                          defaultChecked={editingItem?.active !== false}
                          className="rounded border-gray-300 text-green-500 focus:ring-green-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Producto Disponible</span>
                      </label>
                    </div>
                  </>
                )}

                {/* Image Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Imágenes (Máximo 4 fotos)</label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    disabled={uploadingImage || imagePreview.length >= 4}
                    className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 disabled:opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Puedes subir hasta 4 imágenes. Formatos: JPG, PNG, GIF. Máximo 5MB por imagen.
                  </p>

                  {uploadingImage && (
                    <div className="mt-2 text-sm text-gray-500 flex items-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i> Subiendo imágenes...
                    </div>
                  )}

                  {/* Image Preview Gallery */}
                  {imagePreview.length > 0 && (
                    <div className="mt-4">
                      <div className="grid grid-cols-2 gap-2">
                        {imagePreview.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={image}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-24 object-cover rounded-md border"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                            >
                              <i className="ri-close-line"></i>
                            </button>
                            {index === 0 && (
                              <div className="absolute bottom-1 left-1 bg-green-500 text-white px-2 py-1 rounded text-xs">
                                Principal
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={uploadingImage}
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${
                      uploadingImage
                        ? 'bg-gray-400 cursor-not-allowed'
                        : modalType === 'mision'
                        ? 'bg-purple-500 hover:bg-purple-600'
                        : 'bg-green-500 hover:bg-green-600'
                    }`}
                  >
                    {uploadingImage ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i> Subiendo...
                      </>
                    ) : (
                      `Guardar ${modalType === 'mision' ? 'Misión' : 'Producto'}`
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
